// Funnel telemetry for the newsletter test-scan campaign.
//
// Six beacons per scan, POSTed to hezo-ops:
//
//   POST {VITE_HEZO_OPS_BASE_URL}/api/scan-sessions/<jwt>/events
//   body { type, scanID?, detail? }  →  204, no body
//
// Contract + rationale live in the hezo-ops handoff
// `_handoffs/hezo-ops__handoff-funnel-telemetrie-endpunkt.md` (subtask 2).
// The server upserts on [scanId, type], so a repeated beacon bumps a counter
// instead of creating a duplicate row. Retries are free, and the client needs
// no de-duplication of its own for correctness (App still guards the once-only
// events so `count > 1` keeps meaning "retry").
//
// Strictly fire-and-forget: nothing here is awaited on a render path, nothing
// throws, no result is inspected. A broken or absent endpoint must never stall
// or fail the capture flow. The SPA's job is the scan, not the metric.
//
// Privacy: no cookies, no third-party tracker, no user-agent and no IP in the
// body. `detail` carries small, purpose-bound facts only (shot count, trigger
// outcome). The JWT in the path is the same credential the rest of the client
// already sends; it is not an extra identifier.

import { devCaptureEnabled } from "./config";

/** The six funnel steps. Names are the server-side whitelist: an unknown
 *  value is rejected with 400 and written nowhere, so these must match
 *  hezo-ops exactly. */
export const FUNNEL_EVENT_TYPES = [
  "session_loaded",
  "consent_accepted",
  "capture_started",
  "shots_complete",
  "uploads_complete",
  "process_triggered",
] as const;

export type FunnelEventType = (typeof FUNNEL_EVENT_TYPES)[number];

export interface FunnelEvent {
  type: FunnelEventType;
  /** Optional: the server resolves the scan from the token for baked-in
   *  (campaign / legacy) invites. Sent anyway so reusable multi-pair invites,
   *  whose token carries only an inviteId, are covered by the same call. */
  scanID?: string;
  /** Small, purpose-bound extra. `{ ok: true }` / `{ ok: false, status,
   *  errors? }` on process_triggered — `errors` is the full list of
   *  footscan-server issue codes from the 422, not just the first — counts
   *  elsewhere. Never free-form client text. */
  detail?: Record<string, unknown>;
}

/**
 * Queue one funnel beacon. Returns immediately and never throws.
 *
 * `navigator.sendBeacon` first: the browser owns the request from there, so it
 * survives the page being torn down in the same moment (a customer closing the
 * tab right after tapping "start" is exactly the drop-off we want to measure).
 * `fetch(..., { keepalive: true })` is the fallback where sendBeacon is missing
 * or refuses to queue, with the same non-blocking, teardown-surviving property.
 *
 * No-ops without a token (dev capture, landing page) and on the dev-capture
 * path, whose synthetic runs would otherwise pollute the campaign funnel.
 *
 * The beacon body is sent as a RAW STRING, not a `Blob` with an explicit
 * `application/json` type. A typed Blob makes this a CORS "non-simple"
 * request, which needs a preflight for the cross-origin call to hezo-ops —
 * and iOS Safari's `sendBeacon` has a real, measured bug where the preflight
 * succeeds (204, correct `access-control-allow-origin`) but the browser
 * never sends the actual POST afterwards, while `sendBeacon()` still
 * returns `true`. That made the early-return below silently drop every
 * single event on a real device (verified 2026-08-21 against staging via a
 * MITM proxy: three OPTIONS preflights, zero POSTs, on a real capture run
 * whose CustomerScan materialised fine). A bare string defaults to
 * `text/plain;charset=UTF-8`, a CORS-safelisted content type that needs no
 * preflight at all. hezo-ops's route parses the body with the
 * Fetch-standard `Request.json()`, which reads the raw bytes regardless of
 * the `Content-Type` header, so the server side needs no matching change.
 */
export function sendFunnelEvent(
  baseUrl: string,
  token: string | null,
  event: FunnelEvent,
): void {
  if (!token) return;
  if (devCaptureEnabled()) return;

  const url = `${baseUrl}/api/scan-sessions/${encodeURIComponent(token)}/events`;
  const payload = JSON.stringify({
    type: event.type,
    ...(event.scanID ? { scanID: event.scanID } : {}),
    ...(event.detail ? { detail: event.detail } : {}),
  });

  try {
    const beacon = navigator.sendBeacon?.bind(navigator);
    if (beacon && beacon(url, payload)) {
      return;
    }
    // Fallback. The rejection handler is the point of this call: an offline
    // device or a not-yet-deployed endpoint must stay silent, not surface an
    // unhandled rejection mid-capture.
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget: a telemetry miss is not a scan failure */
    });
  } catch {
    /* sendBeacon/fetch unavailable or threw synchronously: drop the event */
  }
}

/**
 * A per-page-load reporter that drops repeats of the once-per-scan steps.
 *
 * The server upserts, so a repeat is never a duplicate row, it bumps `count`.
 * That column is meant to read as "the beacon was retried"; five of the six
 * steps happen once per scan by definition, so re-sending them would blur it.
 * `process_triggered` is deliberately exempt: re-firing it from the DoneScreen
 * retry is exactly the repetition the counter should show, and its `detail`
 * (last attempt wins) is what makes a recovered failure visible.
 *
 * `getToken` is read per call, not captured: the token arrives from the URL
 * during the first effect, after the reporter is created.
 */
export function createFunnelReporter(
  baseUrl: string,
  getToken: () => string | null,
): (type: FunnelEventType, scanID: string, detail?: Record<string, unknown>) => void {
  const sent = new Set<FunnelEventType>();
  return (type, scanID, detail) => {
    if (type !== "process_triggered") {
      if (sent.has(type)) return;
      sent.add(type);
    }
    sendFunnelEvent(baseUrl, getToken(), { type, scanID, detail });
  };
}
